/**
 * Campaigns (pivot doc §5, §6 Stage 2): a campaign, its online and offline
 * channels, each channel's creative, and each channel's tracking — UTM-tagged
 * URL, short link, QR code and promo code — so an offline channel attributes
 * exactly like a digital one.
 *
 * The vocabularies mirror `api/.../modules/campaigns/vocabulary.py`; the wire
 * section at the bottom mirrors `schemas.py` and is the only place that knows
 * the API's field names. Screens speak the camel-case domain types above it.
 *
 * Three things the brief asks for that the API has no column for travel as
 * references in the lists it does have, prefixed so they never collide with a
 * segment or automation id: geography as `geo:<place>` in the audience include
 * list, and automation triggers as `trigger:<trigger>:<action>` in
 * `automation_refs`. Anything else found in those lists is kept untouched.
 */

import type { DrillDown } from '@/lib/drilldown'
import type { GlobalFilters } from '@/lib/filters'

import type { ChannelKind, Envelope, Environment } from './types'

export type { ChannelKind }

// --- objectives ----------------------------------------------------------------

/** The objectives the create flow offers (the brief's list). */
export const CAMPAIGN_OBJECTIVES = [
  'drive_entries',
  'increase_revenue',
  'acquire_customers',
  'retain_customers',
  'grow_audience',
  'brand_awareness',
  'event_promotion',
  'custom',
] as const
export type CampaignObjective = (typeof CAMPAIGN_OBJECTIVES)[number]

export const OBJECTIVE_LABELS: Record<CampaignObjective, string> = {
  drive_entries: 'Drive entries',
  increase_revenue: 'Increase revenue',
  acquire_customers: 'Acquire customers',
  retain_customers: 'Retain customers',
  grow_audience: 'Grow audience',
  brand_awareness: 'Brand awareness',
  event_promotion: 'Event promotion',
  custom: 'Custom',
}

export const OBJECTIVE_DESCRIPTIONS: Record<CampaignObjective, string> = {
  drive_entries: 'Sell more entries into the linked competitions.',
  increase_revenue: 'Grow revenue and order value across the site.',
  acquire_customers: 'Bring in first-time customers.',
  retain_customers: 'Bring existing customers back.',
  grow_audience: 'Build traffic, followers and subscribers.',
  brand_awareness: 'Put the brand in front of new people.',
  event_promotion: 'Promote a launch, draw or live event.',
  custom: 'Describe your own objective.',
}

/** `CampaignObjective` in `vocabulary.py`. */
export const API_OBJECTIVES = [
  'awareness',
  'traffic',
  'sales',
  'registrations',
  'launch',
  'sell_out',
  'reactivation',
  'retention',
] as const
export type ApiObjective = (typeof API_OBJECTIVES)[number]

/** How each offered objective is stored. `custom` has no API value yet. */
export const OBJECTIVE_TO_API: Record<Exclude<CampaignObjective, 'custom'>, ApiObjective> = {
  drive_entries: 'sell_out',
  increase_revenue: 'sales',
  acquire_customers: 'registrations',
  retain_customers: 'retention',
  grow_audience: 'traffic',
  brand_awareness: 'awareness',
  event_promotion: 'launch',
}

/** The objectives a client can store. The API has no value for `custom` yet, so the live client does not offer it. */
export function objectivesFor(kind: 'mock' | 'http'): readonly CampaignObjective[] {
  return kind === 'http' ? CAMPAIGN_OBJECTIVES.filter((objective) => objective !== 'custom') : CAMPAIGN_OBJECTIVES
}

export function objectiveFromApi(value: string): CampaignObjective {
  if (value === 'reactivation') return 'retain_customers'
  const match = (Object.entries(OBJECTIVE_TO_API) as Array<[CampaignObjective, ApiObjective]>).find(
    ([, api]) => api === value,
  )
  return match ? match[0] : 'custom'
}

// --- statuses ------------------------------------------------------------------

export const CAMPAIGN_STATUSES = ['draft', 'scheduled', 'active', 'paused', 'completed', 'archived'] as const
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number]

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  archived: 'Archived',
}

/**
 * The status a campaign takes when it is launched or resumed on `today`: a
 * draft or paused campaign whose start date is still ahead is scheduled, not
 * live. Anything else — including "start now" on a scheduled campaign — goes
 * active as asked.
 */
export function activationStatus(current: CampaignStatus | null, startsAt: string | null | undefined, today: string): CampaignStatus {
  const waiting = current === null || current === 'draft' || current === 'paused'
  return waiting && startsAt && startsAt > today ? 'scheduled' : 'active'
}

export const CHANNEL_STATUSES = ['draft', 'active', 'paused', 'completed', 'archived'] as const
export type ChannelStatus = (typeof CHANNEL_STATUSES)[number]

export const CHANNEL_STATUS_LABELS: Record<ChannelStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  archived: 'Archived',
}

export const TRACKING_STATUSES = ['active', 'paused', 'archived'] as const
export type TrackingStatus = (typeof TRACKING_STATUSES)[number]

export const TRACKING_STATUS_LABELS: Record<TrackingStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  archived: 'Archived',
}

// --- channels --------------------------------------------------------------------

/** `ChannelType` in `vocabulary.py`: digital first, then offline, then custom. */
export const CHANNEL_TYPES = [
  'meta_ads',
  'google_ads',
  'tiktok_ads',
  'email',
  'push',
  'organic_social',
  'referral',
  'billboard',
  'flyer',
  'poster',
  'event',
  'exhibition',
  'popup_stand',
  'venue_screen',
  'in_store_signage',
  'partner_venue',
  'radio',
  'custom',
] as const
export type ChannelType = (typeof CHANNEL_TYPES)[number]

export function isChannelType(value: string): value is ChannelType {
  return (CHANNEL_TYPES as readonly string[]).includes(value)
}

export const CHANNEL_TYPE_LABELS: Record<ChannelType, string> = {
  meta_ads: 'Meta Ads',
  google_ads: 'Google Ads',
  tiktok_ads: 'TikTok',
  email: 'Email',
  push: 'Push',
  organic_social: 'Organic social',
  referral: 'Referral / affiliate',
  billboard: 'Electronic billboards',
  flyer: 'Printed flyers',
  poster: 'Posters',
  event: 'Events',
  exhibition: 'Exhibitions',
  popup_stand: 'Pop-up stands',
  venue_screen: 'Venue screens',
  in_store_signage: 'In-store signage',
  partner_venue: 'Partner venues',
  radio: 'Radio',
  custom: 'Custom',
}

/** `CHANNEL_KIND`: the kind each type implies. `custom` has none; its creator says. */
export const CHANNEL_KIND: Record<Exclude<ChannelType, 'custom'>, ChannelKind> = {
  meta_ads: 'digital',
  google_ads: 'digital',
  tiktok_ads: 'digital',
  email: 'digital',
  push: 'digital',
  organic_social: 'digital',
  referral: 'digital',
  billboard: 'offline',
  flyer: 'offline',
  poster: 'offline',
  event: 'offline',
  exhibition: 'offline',
  popup_stand: 'offline',
  venue_screen: 'offline',
  in_store_signage: 'offline',
  partner_venue: 'offline',
  radio: 'offline',
}

export function kindOf(type: ChannelType, custom: ChannelKind = 'digital'): ChannelKind {
  return type === 'custom' ? custom : CHANNEL_KIND[type]
}

export const DIGITAL_CHANNEL_TYPES = CHANNEL_TYPES.filter(
  (type): type is Exclude<ChannelType, 'custom'> => type !== 'custom' && CHANNEL_KIND[type] === 'digital',
)
export const OFFLINE_CHANNEL_TYPES = CHANNEL_TYPES.filter(
  (type): type is Exclude<ChannelType, 'custom'> => type !== 'custom' && CHANNEL_KIND[type] === 'offline',
)

/** Channels the business owns outright: no media budget to plan. */
export const OWNED_CHANNEL_TYPES: readonly ChannelType[] = ['email', 'push', 'organic_social']

export function isOwnedChannel(type: ChannelType): boolean {
  return OWNED_CHANNEL_TYPES.includes(type)
}

/** `CHANNEL_UTM`: default `utm_source` / `utm_medium`. Offline channels share the `offline` medium. */
export const CHANNEL_UTM: Record<Exclude<ChannelType, 'custom'>, readonly [string, string]> = {
  meta_ads: ['facebook', 'paid_social'],
  google_ads: ['google', 'cpc'],
  tiktok_ads: ['tiktok', 'paid_social'],
  email: ['email', 'email'],
  push: ['push', 'push'],
  organic_social: ['social', 'organic_social'],
  referral: ['referral', 'referral'],
  billboard: ['billboard', 'offline'],
  flyer: ['flyer', 'offline'],
  poster: ['poster', 'offline'],
  event: ['event', 'offline'],
  exhibition: ['exhibition', 'offline'],
  popup_stand: ['popup_stand', 'offline'],
  venue_screen: ['venue_screen', 'offline'],
  in_store_signage: ['in_store_signage', 'offline'],
  partner_venue: ['partner_venue', 'offline'],
  radio: ['radio', 'offline'],
}

export function defaultUtm(type: ChannelType, kind: ChannelKind): { source: string; medium: string } {
  const [source, medium] = type === 'custom' ? ['custom', kind] : CHANNEL_UTM[type]
  return { source, medium }
}

// --- creative ----------------------------------------------------------------------

export const CREATIVE_TYPES = [
  'meta_creative',
  'google_ad',
  'tiktok_ad',
  'email_content',
  'push_copy',
  'social_post',
  'referral_asset',
  'billboard_asset',
  'flyer_design',
  'poster_design',
  'event_asset',
  'exhibition_asset',
  'popup_stand_asset',
  'venue_screen_asset',
  'signage_asset',
  'partner_venue_asset',
  'radio_script',
  'custom',
] as const
export type CreativeType = (typeof CREATIVE_TYPES)[number]

export type CreativeField = 'headline' | 'body' | 'assetUrl'

export interface CreativeSlot {
  creativeType: CreativeType
  label: string
  /** The fields this kind of asset has, with their labels. */
  fields: Partial<Record<CreativeField, string>>
  hint: string
}

/** One asset slot per channel, of a creative type `CHANNEL_CREATIVES` allows there. */
export const CREATIVE_SLOTS: Record<ChannelType, CreativeSlot> = {
  meta_ads: { creativeType: 'meta_creative', label: 'Meta ad', fields: { headline: 'Headline', body: 'Primary text', assetUrl: 'Image or video URL' }, hint: '1080×1080 image or a 15s vertical video.' },
  google_ads: { creativeType: 'google_ad', label: 'Search ad', fields: { headline: 'Headline', body: 'Description' }, hint: 'Up to 30 characters per headline, 90 per description.' },
  tiktok_ads: { creativeType: 'tiktok_ad', label: 'TikTok ad', fields: { body: 'Caption', assetUrl: 'Video URL' }, hint: '9:16 video, 15–60 seconds.' },
  email: { creativeType: 'email_content', label: 'Email', fields: { headline: 'Subject line', body: 'Body' }, hint: 'Keep the subject under 50 characters.' },
  push: { creativeType: 'push_copy', label: 'Push notification', fields: { headline: 'Title', body: 'Message' }, hint: 'Title under 40 characters, message under 120.' },
  organic_social: { creativeType: 'social_post', label: 'Social post', fields: { body: 'Post copy', assetUrl: 'Image or video URL' }, hint: 'Put the short link in the bio or first comment.' },
  referral: { creativeType: 'referral_asset', label: 'Referral asset', fields: { headline: 'Offer', body: 'Partner copy', assetUrl: 'Banner URL' }, hint: 'Share the tracking URL with the partner.' },
  billboard: { creativeType: 'billboard_asset', label: 'Billboard artwork', fields: { headline: 'Headline', assetUrl: 'Artwork URL' }, hint: 'Large QR code and short link; readable in three seconds.' },
  flyer: { creativeType: 'flyer_design', label: 'Flyer design', fields: { headline: 'Headline', body: 'Copy', assetUrl: 'Print-ready PDF URL' }, hint: 'A5 with bleed; QR code at least 2 cm wide.' },
  poster: { creativeType: 'poster_design', label: 'Poster design', fields: { headline: 'Headline', assetUrl: 'Print-ready PDF URL' }, hint: 'A2 or A1; QR code at least 4 cm wide.' },
  event: { creativeType: 'event_asset', label: 'Event asset', fields: { headline: 'Headline', body: 'Stand copy', assetUrl: 'Asset URL' }, hint: 'Banner, stand graphics or a handout.' },
  exhibition: { creativeType: 'exhibition_asset', label: 'Exhibition asset', fields: { headline: 'Headline', body: 'Stand copy', assetUrl: 'Asset URL' }, hint: 'Stand graphics; bring printed QR codes.' },
  popup_stand: { creativeType: 'popup_stand_asset', label: 'Pop-up stand asset', fields: { headline: 'Headline', assetUrl: 'Asset URL' }, hint: 'Roller banner with the QR code at eye level.' },
  venue_screen: { creativeType: 'venue_screen_asset', label: 'Venue screen loop', fields: { headline: 'Headline', assetUrl: 'Video URL' }, hint: '16:9 loop, 10–20 seconds, QR on screen throughout.' },
  in_store_signage: { creativeType: 'signage_asset', label: 'In-store signage', fields: { headline: 'Headline', assetUrl: 'Artwork URL' }, hint: 'Counter card or window sticker.' },
  partner_venue: { creativeType: 'partner_venue_asset', label: 'Partner venue asset', fields: { headline: 'Headline', body: 'Copy', assetUrl: 'Asset URL' }, hint: 'Table talkers or posters for the partner.' },
  radio: { creativeType: 'radio_script', label: 'Radio script', fields: { body: 'Script' }, hint: 'Read out the promo code and the short link.' },
  custom: { creativeType: 'custom', label: 'Custom asset', fields: { headline: 'Headline', body: 'Copy', assetUrl: 'Asset URL' }, hint: 'Anything that carries the tracking link or code.' },
}

// --- audience and automation --------------------------------------------------------

export interface AudienceSegment {
  id: string
  label: string
}

/** The segments a campaign can target until the Audiences module serves its own. */
export const AUDIENCE_SEGMENTS: readonly AudienceSegment[] = [
  { id: 'seg_new_customers', label: 'New customers' },
  { id: 'seg_returning_customers', label: 'Returning customers' },
  { id: 'seg_high_value', label: 'High-value customers' },
  { id: 'seg_lapsed_60d', label: 'Lapsed for 60+ days' },
  { id: 'seg_newsletter', label: 'Newsletter subscribers' },
  { id: 'seg_push_opted_in', label: 'Push opted in' },
  { id: 'seg_basket_abandoners', label: 'Abandoned a basket (7 days)' },
  { id: 'seg_recent_winners', label: 'Recent winners' },
]

export function segmentLabel(id: string): string {
  return AUDIENCE_SEGMENTS.find((segment) => segment.id === id)?.label ?? id
}

export const AUTOMATION_TRIGGERS = [
  { key: 'competition_sold_80', label: 'A linked competition reaches 80% sold' },
  { key: 'competition_behind_pace', label: 'A linked competition falls behind pace' },
  { key: 'closing_24h', label: '24 hours before a linked competition closes' },
  { key: 'channel_roas_below_1', label: 'A paid channel’s ROAS drops below 1×' },
  { key: 'budget_80_spent', label: '80% of the campaign budget is spent' },
  { key: 'promo_code_first_use', label: 'A promo code is used for the first time' },
] as const
export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[number]['key']

export const AUTOMATION_ACTIONS = [
  { key: 'notify_team', label: 'Notify the team' },
  { key: 'create_alert', label: 'Create an alert' },
  { key: 'send_email', label: 'Send an email' },
  { key: 'send_push', label: 'Send a push notification' },
  { key: 'pause_channel', label: 'Pause the channel' },
] as const
export type AutomationAction = (typeof AUTOMATION_ACTIONS)[number]['key']

export interface AutomationRule {
  trigger: AutomationTrigger
  action: AutomationAction
}

export function triggerLabel(key: AutomationTrigger): string {
  return AUTOMATION_TRIGGERS.find((trigger) => trigger.key === key)?.label ?? key
}

export function actionLabel(key: AutomationAction): string {
  return AUTOMATION_ACTIONS.find((action) => action.key === key)?.label ?? key
}

// --- the domain ----------------------------------------------------------------------

export interface CampaignAudience {
  include: string[]
  exclude: string[]
  /** Places, as typed: "Manchester", "North West". */
  geography: string[]
}

export interface Campaign {
  id: string
  websiteId: string
  name: string
  slug: string
  description: string | null
  objective: CampaignObjective
  /** The objective in the user's words, when it is `custom`. */
  customObjective: string | null
  status: CampaignStatus
  competitionIds: string[]
  /** ISO dates. */
  startsAt: string | null
  endsAt: string | null
  totalBudget: number | null
  currency: string
  audience: CampaignAudience
  automations: AutomationRule[]
  /** Automation references this app did not create; preserved on every write. */
  otherAutomationRefs: string[]
  createdAt: string
  updatedAt: string
}

export interface CampaignChannel {
  id: string
  campaignId: string
  name: string
  channelType: ChannelType
  kind: ChannelKind
  status: ChannelStatus
  budget: number | null
  spend: number | null
  providerRef: string | null
  createdAt: string
  updatedAt: string
}

export interface CampaignCreative {
  id: string
  campaignId: string
  channelId: string
  name: string
  creativeType: CreativeType
  status: ChannelStatus
  headline: string | null
  body: string | null
  assetUrl: string | null
  createdAt: string
  updatedAt: string
}

export interface CampaignTracking {
  id: string
  campaignId: string
  channelId: string
  creativeId: string | null
  utmSource: string
  utmMedium: string
  utmCampaign: string
  utmContent: string | null
  landingPath: string | null
  trackingUrl: string
  shortCode: string | null
  /** Null until minted on the tenant's own host. */
  shortUrl: string | null
  qrCode: string | null
  qrUrl: string | null
  promoCode: string | null
  status: TrackingStatus
  createdAt: string
  updatedAt: string
}

export interface CampaignActivity {
  id: string
  at: string
  message: string
}

export interface CampaignDetail {
  campaign: Campaign
  channels: CampaignChannel[]
  creatives: CampaignCreative[]
  tracking: CampaignTracking[]
  /** Newest first. */
  activity: CampaignActivity[]
}

/** What each number means is defined once, in the API's `performance.py`. */
export interface PerformanceMetrics {
  /** Short links opened. */
  visits: number
  /** QR codes scanned. */
  scans: number
  /** Clicks on UTM-tagged links. */
  clicks: number
  promoCodeUses: number
  orders: number
  entries: number
  customers: number
  revenue: number
  spend: number | null
  conversionRate: number | null
  costPerAcquisition: number | null
  returnOnAdSpend: number | null
}

export interface ChannelPerformanceRow extends PerformanceMetrics {
  channelId: string
  name: string
  channelType: ChannelType
  kind: ChannelKind
  drillDown: DrillDown
}

export interface CampaignPerformance {
  campaignId: string
  currency: string
  start: string
  end: string
  nonProduction: boolean
  channels: ChannelPerformanceRow[]
  totals: PerformanceMetrics
}

export interface CampaignListItem {
  campaign: Campaign
  channels: Array<Pick<CampaignChannel, 'id' | 'name' | 'channelType' | 'kind'>>
  totals: PerformanceMetrics
  /** The campaign loaded but its channels did not; `channels` is then empty, not "no channels". */
  channelsUnavailable?: boolean
  /** The campaign loaded but its figures did not; `totals` is then empty, not zero. */
  performanceUnavailable?: boolean
}

export interface CampaignWebsite {
  id: string
  name: string
  /** `https://example.com`: where tracking URLs point. */
  origin: string
  environment: Environment
}

export function emptyMetrics(): PerformanceMetrics {
  return {
    visits: 0,
    scans: 0,
    clicks: 0,
    promoCodeUses: 0,
    orders: 0,
    entries: 0,
    customers: 0,
    revenue: 0,
    spend: null,
    conversionRate: null,
    costPerAcquisition: null,
    returnOnAdSpend: null,
  }
}

/** Sums rows and recomputes the ratios from the sums, never by averaging ratios. */
export function sumMetrics(rows: readonly PerformanceMetrics[]): PerformanceMetrics {
  const total = emptyMetrics()
  let spend = 0
  let anySpend = false
  for (const row of rows) {
    total.visits += row.visits
    total.scans += row.scans
    total.clicks += row.clicks
    total.promoCodeUses += row.promoCodeUses
    total.orders += row.orders
    total.entries += row.entries
    total.customers += row.customers
    total.revenue += row.revenue
    if (row.spend !== null) {
      spend += row.spend
      anySpend = true
    }
  }
  total.revenue = Math.round(total.revenue * 100) / 100
  return withRatios(total, anySpend ? Math.round(spend * 100) / 100 : null)
}

export function withRatios<T extends PerformanceMetrics>(metrics: T, spend: number | null): T {
  const interactions = metrics.visits + metrics.scans + metrics.clicks
  return {
    ...metrics,
    spend,
    conversionRate: interactions > 0 ? metrics.orders / interactions : null,
    costPerAcquisition: spend !== null && spend > 0 && metrics.orders > 0 ? spend / metrics.orders : null,
    returnOnAdSpend: spend !== null && spend > 0 ? metrics.revenue / spend : null,
  }
}

// --- writes ----------------------------------------------------------------------------

export type PromoCodeMode = 'none' | 'generate' | 'existing'

export interface TrackingDraft {
  landingPath: string
  utmSource: string
  utmMedium: string
  utmContent: string
  shortLink: boolean
  qrCode: boolean
  promoMode: PromoCodeMode
  /** A code already set up in the shop, when `promoMode` is `existing`. */
  promoCode: string
}

export interface CreativeDraft {
  headline: string
  body: string
  assetUrl: string
}

export interface ChannelDraft {
  /** Stable within the draft: the type, or `custom-N`. */
  key: string
  channelType: ChannelType
  name: string
  kind: ChannelKind
  budget: number | null
  creative: CreativeDraft
  tracking: TrackingDraft
}

/** Everything the create flow collects, saved in one go. */
export interface CampaignDraft {
  websiteId: string
  name: string
  description: string
  competitionIds: string[]
  startsAt: string
  endsAt: string
  objective: CampaignObjective | null
  customObjective: string
  channels: ChannelDraft[]
  audience: CampaignAudience
  totalBudget: number | null
  automations: AutomationRule[]
}

export type SaveIntent = 'draft' | 'launch'

export interface CampaignPatch {
  name?: string
  description?: string | null
  status?: CampaignStatus
  totalBudget?: number | null
  startsAt?: string | null
  endsAt?: string | null
  competitionIds?: string[]
  audience?: CampaignAudience
  automations?: AutomationRule[]
}

export interface ChannelPatch {
  status?: ChannelStatus
  budget?: number | null
  spend?: number | null
}

export interface CreativePatch {
  headline?: string | null
  body?: string | null
  assetUrl?: string | null
}

export interface TrackingPatch {
  status?: TrackingStatus
  landingPath?: string | null
}

export interface TrackingGenerateInput {
  channelId: string
  shortLink: boolean
  qrCode: boolean
  generatePromoCode: boolean
}

/**
 * The campaign screens' slice of `ApiClient`. Every write answers with the
 * campaign as it now stands, so a screen never shows half an edit.
 */
export interface CampaignsApi {
  /** Where a campaign's tracking can point. */
  campaignWebsites(): Promise<CampaignWebsite[]>
  /** Performance totals cover the global filters' date range and environments. */
  campaigns(filters: GlobalFilters): Promise<Envelope<CampaignListItem[]>>
  campaign(id: string): Promise<Envelope<CampaignDetail | null>>
  campaignPerformance(id: string, filters: GlobalFilters): Promise<Envelope<CampaignPerformance>>
  createCampaign(draft: CampaignDraft, intent: SaveIntent): Promise<Envelope<CampaignDetail>>
  updateCampaign(id: string, patch: CampaignPatch): Promise<Envelope<CampaignDetail>>
  updateCampaignChannel(campaignId: string, channelId: string, patch: ChannelPatch): Promise<Envelope<CampaignDetail>>
  updateCampaignCreative(
    campaignId: string,
    creative: Pick<CampaignCreative, 'id' | 'channelId'>,
    patch: CreativePatch,
  ): Promise<Envelope<CampaignDetail>>
  generateCampaignTracking(campaignId: string, input: TrackingGenerateInput): Promise<Envelope<CampaignDetail>>
  updateCampaignTracking(campaignId: string, trackingId: string, patch: TrackingPatch): Promise<Envelope<CampaignDetail>>
  /** The tracking record's QR code as an SVG document. */
  campaignQrSvg(campaignId: string, trackingId: string): Promise<string>
}

/**
 * A save that stopped part-way: the campaign exists as a draft and should be
 * finished on its own page, never created a second time.
 */
export class CampaignPartiallySavedError extends Error {
  constructor(
    message: string,
    readonly campaignId: string,
  ) {
    super(message)
    this.name = 'CampaignPartiallySavedError'
  }
}

/** `Reference` in `schemas.py`: every audience and automation reference is at most this long. */
export const REFERENCE_MAX = 100

// --- references --------------------------------------------------------------------------

const GEO_PREFIX = 'geo:'
const TRIGGER_PREFIX = 'trigger:'

export function audienceToRefs(audience: CampaignAudience): { include: string[]; exclude: string[] } {
  return {
    include: [...audience.include, ...audience.geography.map((place) => `${GEO_PREFIX}${place}`)],
    exclude: [...audience.exclude],
  }
}

export function audienceFromRefs(include: readonly string[], exclude: readonly string[]): CampaignAudience {
  return {
    include: include.filter((ref) => !ref.startsWith(GEO_PREFIX)),
    exclude: [...exclude],
    geography: include.filter((ref) => ref.startsWith(GEO_PREFIX)).map((ref) => ref.slice(GEO_PREFIX.length)),
  }
}

export function automationRef(rule: AutomationRule): string {
  return `${TRIGGER_PREFIX}${rule.trigger}:${rule.action}`
}

export function automationsFromRefs(refs: readonly string[]): { rules: AutomationRule[]; other: string[] } {
  const rules: AutomationRule[] = []
  const other: string[] = []
  for (const ref of refs) {
    const [prefix, trigger, action] = ref.split(':')
    const knownTrigger = AUTOMATION_TRIGGERS.some((candidate) => candidate.key === trigger)
    const knownAction = AUTOMATION_ACTIONS.some((candidate) => candidate.key === action)
    if (`${prefix}:` === TRIGGER_PREFIX && knownTrigger && knownAction) {
      rules.push({ trigger: trigger as AutomationTrigger, action: action as AutomationAction })
    } else {
      other.push(ref)
    }
  }
  return { rules, other }
}

// --- wire format: api/.../modules/campaigns/schemas.py --------------------------------------

/** Pydantic serialises `Decimal` as a string. */
type WireMoney = string | number | null

export interface CampaignOut {
  id: string
  website_id: string
  environment: Environment
  name: string
  slug: string
  description: string | null
  objective: string
  status: CampaignStatus
  competition_ids: string[]
  starts_at: string | null
  ends_at: string | null
  total_budget: WireMoney
  currency: string
  audience_include_refs: string[]
  audience_exclude_refs: string[]
  automation_refs: string[]
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface ChannelOut {
  id: string
  campaign_id: string
  name: string
  channel_type: string
  kind: ChannelKind
  status: ChannelStatus
  starts_at: string | null
  ends_at: string | null
  budget: WireMoney
  spend: WireMoney
  provider_ref: string | null
  created_at: string
  updated_at: string
}

export interface CreativeOut {
  id: string
  campaign_id: string
  channel_id: string
  name: string
  creative_type: string
  status: ChannelStatus
  headline: string | null
  body: string | null
  asset_url: string | null
  asset_metadata: Record<string, unknown>
  provider_creative_id: string | null
  created_at: string
  updated_at: string
}

export interface TrackingOut {
  id: string
  campaign_id: string
  channel_id: string
  creative_id: string | null
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_content: string | null
  landing_path: string | null
  tracking_url: string
  short_code: string | null
  short_url: string | null
  qr_code: string | null
  qr_url: string | null
  promo_code: string | null
  status: TrackingStatus
  created_at: string
  updated_at: string
}

export interface PerformanceMetricsOut {
  visits: number
  scans: number
  clicks: number
  promo_code_uses: number
  orders: number
  entries: number
  customers: number
  revenue: number
  spend: number | null
  conversion_rate: number | null
  cost_per_acquisition: number | null
  return_on_ad_spend: number | null
}

export interface ChannelPerformanceOut extends PerformanceMetricsOut {
  channel_id: string
  name: string
  channel_type: string
  kind: ChannelKind
}

export interface CampaignPerformanceOut {
  campaign_id: string
  currency: string
  environments: Environment[]
  non_production: boolean
  start: string
  end: string
  channels: ChannelPerformanceOut[]
  totals: PerformanceMetricsOut
}

export interface ListOut<T> {
  items: T[]
}

function money(value: WireMoney): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/** An ISO date from an API timestamp. */
function dateOf(value: string | null): string | null {
  return value ? value.slice(0, 10) : null
}

/** A date as the API's aware datetime: the start or the end of that day, UTC. */
export function toWireDate(value: string | null | undefined, edge: 'start' | 'end'): string | null {
  if (!value) return null
  return `${value}T${edge === 'start' ? '00:00:00' : '23:59:59'}Z`
}

export function campaignFromWire(row: CampaignOut): Campaign {
  const automations = automationsFromRefs(row.automation_refs)
  return {
    id: row.id,
    websiteId: row.website_id,
    name: row.name,
    slug: row.slug,
    description: row.description,
    objective: objectiveFromApi(row.objective),
    customObjective: null,
    status: row.status,
    competitionIds: [...row.competition_ids],
    startsAt: dateOf(row.starts_at),
    endsAt: dateOf(row.ends_at),
    totalBudget: money(row.total_budget),
    currency: row.currency,
    audience: audienceFromRefs(row.audience_include_refs, row.audience_exclude_refs),
    automations: automations.rules,
    otherAutomationRefs: automations.other,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function channelFromWire(row: ChannelOut): CampaignChannel {
  return {
    id: row.id,
    campaignId: row.campaign_id,
    name: row.name,
    channelType: isChannelType(row.channel_type) ? row.channel_type : 'custom',
    kind: row.kind,
    status: row.status,
    budget: money(row.budget),
    spend: money(row.spend),
    providerRef: row.provider_ref,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function creativeFromWire(row: CreativeOut): CampaignCreative {
  return {
    id: row.id,
    campaignId: row.campaign_id,
    channelId: row.channel_id,
    name: row.name,
    creativeType: (CREATIVE_TYPES as readonly string[]).includes(row.creative_type)
      ? (row.creative_type as CreativeType)
      : 'custom',
    status: row.status,
    headline: row.headline,
    body: row.body,
    assetUrl: row.asset_url,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function trackingFromWire(row: TrackingOut): CampaignTracking {
  return {
    id: row.id,
    campaignId: row.campaign_id,
    channelId: row.channel_id,
    creativeId: row.creative_id,
    utmSource: row.utm_source,
    utmMedium: row.utm_medium,
    utmCampaign: row.utm_campaign,
    utmContent: row.utm_content,
    landingPath: row.landing_path,
    trackingUrl: row.tracking_url,
    shortCode: row.short_code,
    shortUrl: row.short_url,
    qrCode: row.qr_code,
    qrUrl: row.qr_url,
    promoCode: row.promo_code,
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function metricsFromWire(row: PerformanceMetricsOut): PerformanceMetrics {
  return {
    visits: row.visits,
    scans: row.scans,
    clicks: row.clicks,
    promoCodeUses: row.promo_code_uses,
    orders: row.orders,
    entries: row.entries,
    customers: row.customers,
    revenue: row.revenue,
    spend: row.spend,
    conversionRate: row.conversion_rate,
    costPerAcquisition: row.cost_per_acquisition,
    returnOnAdSpend: row.return_on_ad_spend,
  }
}

/** The drill-down from a campaign's channel row: the channel analysis, scoped to this campaign. */
export function channelDrillDown(campaignId: string, channelType: ChannelType, label: string): DrillDown {
  return {
    path: '/marketing/channels',
    entity: { type: 'channel', id: channelType, label },
    params: { campaign: campaignId },
  }
}

export function performanceFromWire(row: CampaignPerformanceOut): CampaignPerformance {
  return {
    campaignId: row.campaign_id,
    currency: row.currency,
    start: row.start,
    end: row.end,
    nonProduction: row.non_production,
    channels: row.channels.map((channel) => {
      const channelType = isChannelType(channel.channel_type) ? channel.channel_type : 'custom'
      return {
        ...metricsFromWire(channel),
        channelId: channel.channel_id,
        name: channel.name,
        channelType,
        kind: channel.kind,
        drillDown: channelDrillDown(row.campaign_id, channelType, channel.name),
      }
    }),
    totals: metricsFromWire(row.totals),
  }
}

/** A campaign's recent history, from the timestamps its records carry. */
export function activityFromRecords(detail: Omit<CampaignDetail, 'activity'>): CampaignActivity[] {
  const { campaign, channels, tracking } = detail
  const channelName = (id: string) => channels.find((channel) => channel.id === id)?.name ?? 'a channel'
  const entries: CampaignActivity[] = [
    { id: `${campaign.id}:created`, at: campaign.createdAt, message: 'Campaign created' },
    ...channels.map((channel) => ({ id: `${channel.id}:created`, at: channel.createdAt, message: `${channel.name} channel added` })),
    ...tracking.map((row) => ({ id: `${row.id}:created`, at: row.createdAt, message: `Tracking generated for ${channelName(row.channelId)}` })),
  ]
  if (campaign.updatedAt > campaign.createdAt) {
    entries.push({
      id: `${campaign.id}:updated`,
      at: campaign.updatedAt,
      message: `Campaign updated — now ${CAMPAIGN_STATUS_LABELS[campaign.status].toLowerCase()}`,
    })
  }
  return entries.sort((a, b) => b.at.localeCompare(a.at) || a.id.localeCompare(b.id))
}
